import React, { MouseEventHandler, useRef } from 'react';
import { isIOS } from 'react-device-detect';

import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { getIconByName } from '../../../MuiIcon/Icon';
import { defaultIconPosition, defaultIconSize, defaultIconSpacing } from '../../../../util/constants';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';
import { getBorderStyle, getDropShadowStyle, getFontFamily, getWidgetOpacity } from '../util';
import { useWidgetAnimation } from '../../../Player/hooks/useWidgetAnimation';

// The 4 corner placements. Corners stack vertically like top/bottom (icon above or
// below the text), but the icon is aligned to the left/right edge via align-self, so
// icon and text stay separate, non-overlapping flex items with `spacing` as the gap.
const CORNER_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const isCornerPosition = (position?: string) => !!position && CORNER_POSITIONS.includes(position);
// The icon sits before the text (top row / left) for these, after it otherwise.
const isIconBeforeText = (position?: string) =>
	position === 'top' || position === 'left' || position === 'top-left' || position === 'top-right';

const generateButtonStyle = (isHover: boolean, clickable: boolean, style: StyleProps) => {
	const {
		textAlignment,
		textLabel: {
			backgroundColor: textBackgroundColor,
			color,
			fontWeight,
			hoverBackgroundColor: textHoverBackgroundColor,
			hoverFontWeight,
			hoverColor,
			font,
			fontSize,
			lineHeight,
			shadowColor,
		},
		transformSettings: { borderRadiusBottom, borderRadiusLeft, borderRadiusRight, borderRadiusTop, paddingBottom, paddingLeft, paddingRight, paddingTop },
		iconConfig: { position } = {},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
	} = style;

	// !! Should change to ONLY use backgroundSettings's props after KK-1411 released a few months
	const BGColor = backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== '#00000000' ? backgroundColor : textBackgroundColor;
	const hoverBGColor =
		hoverBackgroundColor && hoverBackgroundColor !== 'transparent' && hoverBackgroundColor !== '#00000000'
			? hoverBackgroundColor
			: textHoverBackgroundColor;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

	return {
		display: 'flex',
		// left/right lay out in a row; top/bottom and all corners stack in a column.
		flexDirection: position === 'left' || position === 'right' ? ('row' as any) : 'column',
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: (textAlignment || 'center') as 'center',
		lineHeight: lineHeight ? `${lineHeight}%` : 'normal',
		...(shadowColor && shadowColor !== 'transparent' ? { textShadow: `2px 2px ${shadowColor}` } : {}),
		width: '100%',
		height: '100%',
		backgroundColor: isHover ? (hoverBGColor || BGColor) : BGColor,
		color: isHover ? (hoverColor || color) : color,
		fontFamily: getFontFamily(font),
		fontSize: `${fontSize}px`,
		fontWeight: isHover ? (hoverFontWeight ?? fontWeight) : fontWeight,
		cursor: clickable ? 'pointer' : 'default',
		paddingBottom: `${paddingBottom}px`,
		paddingLeft: `${paddingLeft}px`,
		paddingRight: `${paddingRight}px`,
		paddingTop: `${paddingTop}px`,
		borderTopRightRadius: `${borderRadiusTop}px`,
		borderBottomRightRadius: `${borderRadiusRight}px`,
		borderBottomLeftRadius: `${borderRadiusBottom}px`,
		borderTopLeftRadius: `${borderRadiusLeft}px`,
		border: isHover ? (hoverBorder || border) : border
	};
};

const generateWrapperStyle = ({
	style,
	isHover,
	clickable,
}: {
	style: StyleProps;
	isHover: boolean;
	clickable?: boolean;
}) => {
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
	return {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;
};

const generateIconContainerStyle = ({ position, spacing }: { position: string; spacing: string }) => {
	const [vertical, horizontal] = isCornerPosition(position) ? position.split('-') : [];
	return {
		paddingBottom: position === 'top' || vertical === 'top' ? `${spacing}px` : 0,
		paddingTop: position === 'bottom' || vertical === 'bottom' ? `${spacing}px` : 0,
		paddingLeft: position === 'right' ? `${spacing}px` : 0,
		paddingRight: position === 'left' ? `${spacing}px` : 0,
		...(horizontal ? { alignSelf: horizontal === 'left' ? 'flex-start' : 'flex-end' } : {}),
		lineHeight: 0,
	} as React.CSSProperties;
};

// Corners only: the text sits in a wrapper that takes all the space the icon leaves and centres the text in it.
// Without it the icon + text are centred as one block, so the text's height (line height, font size, extra lines)
// pushed the icon away from its corner. Plain flex rather than absolute positioning, which Safari misplaces inside
// a foreignObject. Mirrors the editor's ButtonWidget in qbrick-ui.
const cornerTextContainerStyle = {
	flex: '1 1 auto',
	minHeight: 0,
	alignSelf: 'stretch',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
} as React.CSSProperties;

const generateIconStyle = ({ isHover, size, color, hoverColor }: { isHover: boolean; size: string; color: string; hoverColor: string }) => {
	return {
		fontSize: `${size}px`,
		color: isHover ? (hoverColor || color) : color,
	};
};

const Button = ({
	id,
	style,
	data,
	clickable,
	isGoToSceneClick,
	isHover,
	scenarioId,
	sceneId,
	widgetRef,
	isShowWidget,
	isExiting,
	onExitDone,
	exitTiming,
	onClickExitStart,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
}: DetailWidgetInteractionItem) => {
	const {
		textLabel: { text, color, hoverColor },
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
		iconConfig: { position = defaultIconPosition, size = defaultIconSize, spacing = defaultIconSpacing } = {},
	} = style;

	const {
		settings: { iconKey },
		name,
		type,
	} = data;

	const icon =
		iconKey &&
		getIconByName(iconKey, {
			style: generateIconStyle({ isHover, size, color, hoverColor }),
		});

	const gElementRef = useRef<SVGGElement | null>();
	const textElementRef = useRef<HTMLSpanElement | null>();
	const iconElementRef = useRef<HTMLDivElement | null>();
	const bgElementRef = useRef<HTMLDivElement | null>();
	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, textElementRef, iconElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, exitTiming, onClickExitStart, iconSlideFrom: position === 'top-right' || position === 'bottom-right' ? 'right' : 'left', clickable });

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		event.persist?.();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		// pill/square play the press animation first, then run the action; all other
		// exits run the action immediately.
		triggerClickAnimation(() => onClick(event));
	};

	const handleTouchStart = (event: React.TouchEvent) => {
		event.preventDefault();
		handleWidgetAction(event);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleWidgetAction(event);
		}
	}

	return (<g style={{ transform: `rotate(${rotation ?? 0}deg)`, opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
		<g
			id={id}
			onClick={handleWidgetAction}
			onTouchStart={isIOS ? handleTouchStart : undefined}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
			onKeyDown={handleOnKeyDown}
			tabIndex={0}
			aria-label={text || name}
			role="button"
			style={generateWrapperStyle({ style, isHover, clickable })}
			ref={r => gElementRef.current = r}
		>
			<foreignObject
				aria-hidden="true"
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
			>
				<div ref={r => bgElementRef.current = r} style={generateButtonStyle(isHover, clickable, style)} aria-hidden="true">
					{iconKey && isIconBeforeText(position) && (
						<div ref={r => iconElementRef.current = r} style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
					)}

					{isCornerPosition(position) ? (
						<div style={cornerTextContainerStyle}>
							<span ref={r => textElementRef.current = r} dangerouslySetInnerHTML={{ __html: text }} />
						</div>
					) : (
						<span ref={r => textElementRef.current = r} dangerouslySetInnerHTML={{ __html: text }} />
					)}

					{iconKey && !isIconBeforeText(position) && (
						<div ref={r => iconElementRef.current = r} style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
					)}
				</div>
			</foreignObject>
		</g>
	</g>);
};

export default Button;
